import { lazy, Suspense } from 'react';

const ANNUAL_STATEMENTS_SOFTWARE_ID = 22;
import { Outlet } from 'react-router-dom';
import { Index as HomeIndex } from '../../../pages/HomePage/Index/Index';
import { ProtectedRoute } from '../ProtectedRoute/ProtectedRoute';
import { Home } from '../../../pages/HomePage/SubPages/Home/Home';
import { Privacy } from '../../../pages/HomePage/SubPages/Privacy/Privacy';
import { Index as AdminIndex, GetDefault } from '../../../pages/Admin/Index/Index';
import LoadingIndicator from '../../Global/loading-indicator/loading-indicator';

// Lazy-loaded: pulls in the Draft.js rich-text editor (the `editor-draft` chunk),
// which was executing on every page (nothing in this app is route-split) and
// crashing unrelated pages via a frozen-theme bug in MUI's styled engine.
const ContentMgtIndex = lazy(() =>
  import('../../../pages/AdminContentMgt/Index/Index').then((m) => ({ default: m.Index }))
);
import { Brokerage as BrokerageIndex } from '../../../pages/Brokerage/brokerage';
import { Index as FormsIndex } from '../../../pages/Forms/Index/index';
import { Index as NewsIndex } from '../../../pages/News/Index/Index';
import { Index as SoftwareIndex } from '../../../pages/Software/Index/Index';
import { Index as ToolkitIndex } from '../../../pages/ProductToolkit/SubPages/ProductToolkits/Product-Toolkits';
import { Index as RecruitingIndex } from '../../../pages/Recruiting/Index/Index';
import { Index as ReportsIndex } from '../../../pages/Reports/index/index';
import Training from '../../../pages/Training/Index/Training';
import { TrainingModule } from '../../../pages/TrainingModule/TrainingModule.jsx';
import { Index as MyProfileIndex } from '../../../pages/MyProfile/Index';
import { Index as PoliciesIndex } from '../../../pages/Policies/Index/Index';
import { Index as SecureFileUploaderIndex } from '../../../pages/SecureFileUploader/Index';
import { MItem, Index as MarketingIndex } from '../../../pages/Marketing/Index/Index';
import ISDMarketing from '../../../pages/Admin/Subpages/ISDMarketing/ISDMarketing';
import MarketingList from '../../../pages/Marketing/Components/MarketingList';
import PromoItemList from '../../../pages/Marketing/Components/PromoItemList';
import { PostsList } from '../../../pages/Marketing/Components/PostsList';
import AgencyMgt from '../../../pages/Admin/Subpages/AgencyMgt';
import FailedReg from '../../../pages/Admin/Subpages/FailedRegs';
import MailRoom from '../../../pages/Admin/Subpages/MailRoom';
import ProducerProxy from '../../../pages/Admin/Subpages/ProducerProxy';
import SoftwareList from '../../../pages/Software/Subpages/SoftwareList/SoftwareList';
import DoNotCall from '../../../pages/Software/Subpages/DoNotCall/DoNotCall';
import AnnualStatements from '../../../pages/Software/Subpages/AnnualStatements/AnnualStatements';
import WPM from '../../../pages/WPM/wpm';
import AppPackage from '../../../pages/Software/Subpages/AppPackage/AppPackage';
import MyIssuedApps from '../../../pages/Software/Subpages/MyIssuedApps/MyIssuedApps.jsx';
//import SocialMediaMain from '../../../pages/AdminContentMgt/Subpages/SocialMediaMain';
import { LinkedInCallback } from 'react-linkedin-login-oauth2';
import XCallback from '../../../pages/Marketing/Components/XCallback';
import PowerBi from '../../../pages/Reports/powerBi/powerBi';
import PowerBiDash from '../../../pages/Reports/powerBi/powerBiDash';
//import AgentInformation from '../../../pages/MyProfile/Information';
//import AppointmentsTable from '../../../pages/MyProfile/Appointments';
import RequestClaimant from '../../../pages/Software/Subpages/RequestClaimant/RequestClaimant';
import MyIMO from '../../../pages/MyIMO/MyIMO.jsx';
import Proposal from '../../../pages/Software/Subpages/Proposal/Proposal';
import SecureFileDelivery from '../../../pages/Software/Subpages/SecureFileDelivery/SecureFileDelivery';

// Every route in this table is authenticated: the table is mounted as the `children` of
// the pathless layout route in App.jsx, whose element is the auth guard + UserProvider +
// app shell. Routes that must run WITHOUT a session — /oauth/callback, /Error/*,
// /SignOut/* — are declared there as siblings of that layout route, never here.
//
// Paths stay absolute. A pathless parent contributes no path segment, so react-router
// accepts absolute children under it; a parent with a path would reject them.
const routes = () => {
  return [
    {
      path: '/Home',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<Home />} />,
    },
    {
      path: '/Privacy',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<Privacy />} />,
    },
    {
      path: '/Admin/*',
      element: (
        <>
          <ProtectedRoute
            permissions={['Admin', 'MailRoom', 'InformationServices']}
            element={<AdminIndex />}
          />
          <Outlet />
        </>
      ),
      children: [
        {
          index: true,
          element: <GetDefault />,
        },
        {
          path: 'User',
          element: <ProtectedRoute permissions={['Admin']} element={<MailRoom />} />,
        },
        {
          path: 'Profiles',
          element: <ProtectedRoute permissions={['InformationServices']} element={<MailRoom />} />,
        },
        {
          path: 'Agency',
          element: <ProtectedRoute permissions={['Admin']} element={<AgencyMgt />} />,
        },
        {
          path: 'FailedRegistrations',
          element: <ProtectedRoute permissions={['Admin']} element={<FailedReg />} />,
        },
        {
          path: 'MailRoom',
          element: <ProtectedRoute permissions={['MailRoom']} element={<MailRoom />} />,
        },
        {
          path: 'Producer',
          element: <ProtectedRoute permissions={['Admin']} element={<ProducerProxy />} />,
        },
        {
          path: 'ISDMarketing',
          element: <ProtectedRoute permissions={['ISDMarketing']} element={<ISDMarketing />} />,
        },
      ],
    },
    {
      path: '/ContentMgt/:selectedTab',
      element: (
        <>
          <Suspense fallback={<LoadingIndicator />}>
            <ProtectedRoute
              permissions={['ContentManagement', 'ManagerOnboardingAdmin']}
              element={<ContentMgtIndex />}
            />
          </Suspense>
          <Outlet />
        </>
      ),
      children: [
        {
          path: 'linkedin',
          element: <LinkedInCallback />,
        },
        {
          path: 'x',
          element: <XCallback />,
        },
      ],
    },
    {
      path: '/Admin/ContentMgt/*',
      element: (
        <Suspense fallback={<LoadingIndicator />}>
          <ProtectedRoute
            permissions={['ContentManagement', 'ManagerOnboardingAdmin']}
            element={<ContentMgtIndex />}
          />
        </Suspense>
      ),
    },
    {
      path: '/ISDMarketing/*',
      element: <ProtectedRoute permissions={['ISDMarketing']} element={<ISDMarketing />} />,
    },
    {
      path: '/Brokerage',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<BrokerageIndex />} />,
    },
    {
      path: '/Forms',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<FormsIndex />} />,
    },
    {
      path: '/News/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<NewsIndex />} />,
    },
    {
      path: '/ProductToolkit/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<ToolkitIndex />} />,
    },
    {
      path: '/Onboarding/*',
      element: <ProtectedRoute permissions={['Recruitment']} element={<RecruitingIndex />} />,
    },

    {
      path: '/Reports/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<ReportsIndex />} />,
    },
    {
      path: '/PowerBiDash/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<PowerBiDash />} />,
    },
    {
      path: '/PowerBi/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<PowerBi />} />,
    },
    {
      path: '/Applications/*',
      element: (
        <>
          <ProtectedRoute permissions={['GeneralAccess']} element={<SoftwareIndex />} />
          <Outlet />
        </>
      ),
      children: [
        {
          index: true,
          element: <SoftwareList />,
        },
        {
          path: 'AnnualStatements',
          element: (
            <ProtectedRoute
              permissions={['GeneralAccess']}
              allowedProductsSoftwareID={ANNUAL_STATEMENTS_SOFTWARE_ID}
              element={<AnnualStatements />}
            />
          ),
        },
        {
          path: 'BenefitsSolutionProposal',
          element: <ProtectedRoute permissions={['WPM']} element={<WPM />} />,
        },
        {
          path: 'DoNotCall',
          element: <DoNotCall />,
        },
        {
          path: 'AppPackage/*',
          element: <AppPackage />,
        },
        {
          path: 'MyIssuedApps/*',
          element: <MyIssuedApps />,
        },
        {
          path: 'RequestClaimant',
          element: <RequestClaimant />,
        },
        {
          path: 'Proposal',
          element: <Proposal />,
        },
        {
          path: 'SecureFileDelivery',
          element: <SecureFileDelivery />,
        },
        {
          path: 'SecureFileUploader',
          element: <SecureFileUploaderIndex />,
        },
      ],
    },
    {
      path: '/Training/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<Training />} />,
    },
    {
      path: '/TrainingModule/:trainingModule',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<TrainingModule />} />,
    },
    {
      path: '/MyProfile/*',
      element: (
        <>
          <ProtectedRoute permissions={['GeneralAccess']} element={<MyProfileIndex />} />
          <Outlet />
        </>
      ),
    },
    {
      path: '/MyIMO/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<MyIMO />} />,
    },
    {
      path: '/Policies/*',
      element: (
        <>
          <ProtectedRoute permissions={['GeneralAccess']} element={<PoliciesIndex />} />
          <Outlet />
        </>
      ),
    },
    {
      path: '/Marketing/*',
      element: (
        <>
          <ProtectedRoute permissions={['GeneralAccess']} element={<MarketingIndex />} />
          <Outlet />
        </>
      ),
      children: [
        {
          index: true,
          element: <ProtectedRoute permissions={['GeneralAccess']} element={<MarketingList />} />,
        },
        {
          path: ':item',
          element: (
            <>
              <ProtectedRoute permissions={['GeneralAccess']} element={<MItem />} />
              <Outlet />
            </>
          ),
          children: [
            {
              path: 'approvedmessages',
              element: <ProtectedRoute permissions={['GeneralAccess']} element={<PostsList />} />,
            },
            {
              path: 'linkedin',
              element: <LinkedInCallback />,
            },
            {
              path: 'x',
              element: <XCallback />,
            },
          ],
        },
        {
          path: 'PromotionalItems',
          element: <ProtectedRoute permissions={['GeneralAccess']} element={<PromoItemList />} />,
        },
      ],
    },
    {
      path: '/*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<HomeIndex />} />,
    },
    {
      path: '*',
      element: <ProtectedRoute permissions={['GeneralAccess']} element={<HomeIndex />} />,
    },
  ];
};

export default routes;
